import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  BrowserAuthError,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest
} from '@azure/msal-browser';
import { firstValueFrom, filter, timeout } from 'rxjs';

import { ENVIRONMENT } from '@env/environment';

@Injectable()
export class AuthService {
  readonly #authService = inject(MsalService);
  readonly #broadcastService = inject(MsalBroadcastService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);

  readonly CONFIG = ENVIRONMENT.azureAdB2C;

  // DOCS
  // https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/samples/msal-angular-samples
  // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/samples/msal-angular-samples/angular-standalone-sample/src/app/app.component.ts

  async init(): Promise<void> {
    // Skip localStorage cleanup when running inside MSAL's hidden silent-auth iframe.
    // localStorage is shared across the same origin, so clearing it here would also
    // wipe any in-flight state the parent frame is relying on.
    if (!this.isInsideIframe()) {
      this.clearStaleMsalLocalStorage();
    }

    try {
      // timeout(15_000): handleRedirectObservable() can hang indefinitely if MSAL's
      // internal state becomes inconsistent (e.g. a deadlock between navigateInternal()
      // and MsalGuard waiting for inProgress$ = None). The timeout ensures APP_INITIALIZER
      // never blocks forever — a TimeoutError falls through to forceLoginRedirect(),
      // which clears all state and sends the user to B2C for a clean login.
      await firstValueFrom(
        this.#authService.handleRedirectObservable().pipe(timeout(15_000))
      );
    } catch (error) {
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === 'block_iframe_reload'
      ) {
        return;
      }
      await this.forceLoginRedirect();

      return;
    }

    this.#authService.instance.enableAccountStorageEvents();

    this.#broadcastService.msalSubject$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe(() => {
        if (this.#authService.instance.getAllAccounts().length === 0) {
          void this.#router.navigate(['/']);
        }
      });

    this.#broadcastService.inProgress$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  signOut(): void {
    this.#authService.logoutRedirect();
  }

  getActiveAccount(): AccountInfo | null {
    return this.#authService.instance.getActiveAccount();
  }

  getUserEmail(): string | null {
    const activeAccount = this.getActiveAccount();
    if (!activeAccount?.idTokenClaims) {
      return null;
    }
    return (activeAccount.idTokenClaims['email'] as string) || null;
  }

  // #region Private Methods

  // Migration guard: app uses sessionStorage exclusively; clear any stale localStorage data.
  private clearStaleMsalLocalStorage(): void {
    if (localStorage.length > 0) {
      localStorage.clear();
    }
  }

  // True when the app is bootstrapped inside MSAL's hidden silent-auth iframe.
  private isInsideIframe(): boolean {
    return window.self !== window.top;
  }

  // Clears all MSAL state and redirects to B2C for a clean login.
  // loginRedirect() navigates the browser away — the returned Promise never resolves.
  async forceLoginRedirect(): Promise<void> {
    await this.#authService.instance.clearCache();
    await this.#authService.instance.loginRedirect({
      scopes: ENVIRONMENT.coreApi.scopes,
      authority: this.CONFIG.b2cPolicies.authorities.signUpSignIn.authority
    });
    // loginRedirect navigates the browser away. Block here so the Angular router
    // never starts in the window between loginRedirect() resolving and the
    // browser actually leaving — which would trigger the MSAL interceptor while
    // interaction_in_progress is set.
    await new Promise<void>(() => {});
  }

  private checkAndSetActiveAccount(): void {
    const activeAccount = this.#authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.#authService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.#authService.instance.getAllAccounts();
      this.#authService.instance.setActiveAccount(accounts[0]);
    }
  }

  async changePassword(email: string): Promise<void> {
    const request: RedirectRequest = {
      authority: this.CONFIG.b2cPolicies.authorities.signUpSignIn.authority,
      scopes: ENVIRONMENT.coreApi.scopes,
      loginHint: email,
      extraQueryParameters: {
        isCreatePassword: 'true'
      },
      redirectStartPage: ENVIRONMENT.appRoot,
      prompt: 'login'
    };

    await this.#authService.instance.loginRedirect(request);
    await new Promise<void>(() => {});
  }

  // #endregion
}
